/**
 * CorruptedNode — Phase 2 interactive for the Boiler-Bound Sundew apex
 * (`docs/specs/SPEC-conservatory-rebuild.md` §5.2).
 *
 * Persistent heavy mist covers the Sundew in phase 2 — the corrupted
 * nodes are only fully legible when Minna holds Aura Sight. Basil
 * unscrews them via Turn-Table Touch: each node takes 4 hits to clear.
 * Hits landed while Aura Sight is NOT active only count at an effective
 * 50% rate, matching the spec's "phase offset" failure mode.
 *
 * The 50% rate is implemented as: every other rejected-unaura hit is
 * counted toward the clear threshold. That way a player drilling the
 * same node without Aura Sight still makes (slow) progress rather than
 * silently wasting input, but the attractor clearly rewards coordinated
 * Aura Sight + Turn-Table Touch.
 */
import { Scene } from "@babylonjs/core/scene";
import { Vector3 } from "@babylonjs/core/Maths/math.vector";
import { AbstractMesh } from "@babylonjs/core/Meshes/abstractMesh";
import { MeshBuilder } from "@babylonjs/core/Meshes/meshBuilder";
import { Mesh } from "@babylonjs/core/Meshes/mesh";
import { StandardMaterial } from "@babylonjs/core/Materials/standardMaterial";
import { Color3 } from "@babylonjs/core/Maths/math.color";
import { PointLight } from "@babylonjs/core/Lights/pointLight";
import { InteractiveObject, InteractiveConfig } from "./InteractiveObject";
import { gameEvents } from "../../core/EventBus";
import { GameEvents } from "../../config/events";
import { COLORS } from "../../config/GameConfig";

export interface CorruptedNodeConfig extends Omit<InteractiveConfig, "size"> {
  size?: Vector3;
  /** Hits required to clear the node. Default 4. */
  requiredHits?: number;
  /** Presentation only: logical target size/position and hit rules stay fixed. */
  visualScale?: number;
  visualDepth?: number;
}

/** Hits required to clear a node by default. Spec §5.2 calls for 4. */
const DEFAULT_REQUIRED_HITS = 4;

/** Radius of the orb body in world units. */
const ORB_RADIUS = 0.6;

export class CorruptedNode extends InteractiveObject {
  isAuraRevealed = false;

  private readonly requiredHits: number;
  private readonly presentationScale: number;
  private cleared = false;
  private currentHits = 0;
  private missedHits = 0;
  private pulseTime = 0;

  private orbMesh: Mesh;
  private orbMat: StandardMaterial;
  private coreMesh: Mesh;
  private coreMat: StandardMaterial;
  private glowLight: PointLight;
  private presentationEnabled = true;
  private disposed = false;

  constructor(scene: Scene, config: CorruptedNodeConfig) {
    const size =
      config.size ?? new Vector3(ORB_RADIUS * 2, ORB_RADIUS * 2, ORB_RADIUS * 2);
    super(scene, { ...config, size });

    this.requiredHits = config.requiredHits ?? DEFAULT_REQUIRED_HITS;
    this.presentationScale = config.visualScale ?? 1;

    this.orbMesh = MeshBuilder.CreateSphere(
      `corruptedNode_${config.key}_orb`,
      { diameter: ORB_RADIUS * 2, segments: 18 },
      scene,
    );
    this.orbMesh.parent = this.rootMesh;
    this.orbMesh.position = new Vector3(0,0,config.visualDepth ?? 0);
    this.orbMesh.scaling.setAll(this.presentationScale);

    this.orbMat = this.material;
    this.orbMat.name = `corruptedNode_${config.key}_orbMat`;
    this.orbMat.diffuseColor = Color3.FromHexString("#1a0f1f");
    this.orbMat.specularColor = Color3.Black();
    this.orbMat.emissiveColor = Color3.FromHexString(
      COLORS.BIOLUMINESCENT_GREEN,
    ).scale(0.08);
    this.orbMat.alpha = 0.92;
    this.orbMesh.material = this.orbMat;

    this.coreMesh = MeshBuilder.CreateSphere(
      `corruptedNode_${config.key}_core`,
      { diameter: ORB_RADIUS * 0.9, segments: 12 },
      scene,
    );
    this.coreMesh.parent = this.rootMesh;
    this.coreMesh.position = this.orbMesh.position.clone();
    this.coreMesh.scaling.copyFrom(this.orbMesh.scaling);

    this.coreMat = new StandardMaterial(
      `corruptedNode_${config.key}_coreMat`,
      scene,
    );
    this.coreMat.diffuseColor = Color3.FromHexString(
      COLORS.BIOLUMINESCENT_GREEN,
    ).scale(0.5);
    this.coreMat.emissiveColor = Color3.FromHexString(
      COLORS.BIOLUMINESCENT_GREEN,
    ).scale(0.2);
    this.coreMat.specularColor = Color3.Black();
    this.coreMat.alpha = 0.6;
    this.coreMesh.material = this.coreMat;

    this.glowLight = new PointLight(
      `corruptedNode_${config.key}_light`,
      this.rootMesh.position.add(this.orbMesh.position),
      scene,
    );
    this.glowLight.diffuse = Color3.FromHexString(COLORS.BIOLUMINESCENT_GREEN);
    this.glowLight.intensity = .25 * this.presentationScale;
    this.glowLight.range = 2.4 * Math.sqrt(this.presentationScale);

    this.material = this.orbMat;
  }

  /** Puzzle phase owns visibility; Aura reveal cannot re-enable the root. */
  setPresentationEnabled(enabled: boolean): void {
    if (this.disposed) return;
    this.presentationEnabled = enabled;
    this.rootMesh.setEnabled(enabled);
    this.glowLight.intensity = enabled ? (this.cleared ? .1 : .25) * this.presentationScale : 0;
  }

  onInteract(abilityId: string): boolean {
    if (abilityId === "turnTableTouch") {
      return this.registerHit();
    }
    return false;
  }

  /**
   * Called by the Sundew puzzle on every successful Turn-Table Touch
   * rhythm hit while Basil is in range. Returns `true` when the hit
   * made progress toward clearing the node.
   *
   * While `isAuraRevealed === true` every hit counts; otherwise hits
   * land at 50% effective rate via `missedHits % 2 === 0`.
   */
  registerHit(): boolean {
    if (this.cleared) return false;

    let counted = false;
    if (this.isAuraRevealed) {
      this.currentHits += 1;
      counted = true;
    } else {
      this.missedHits += 1;
      if (this.missedHits % 2 === 0) {
        this.currentHits += 1;
        counted = true;
      }
    }

    if (this.currentHits >= this.requiredHits) {
      this.clearNode();
      return true;
    }
    return counted;
  }

  override getHighlightTarget(): AbstractMesh {
    return this.orbMesh;
  }

  get hitsRemaining(): number {
    if (this.cleared) return 0;
    return Math.max(0, this.requiredHits - this.currentHits);
  }

  get isCleared(): boolean {
    return this.cleared;
  }

  tickPulse(dt: number): void {
    if (this.cleared) return;
    this.pulseTime += dt;
    const pulse = Math.sin(this.pulseTime * 3.2) * 0.5 + 0.5;
    const revealBoost = this.isAuraRevealed ? 1.8 : 1.0;
    const base = Color3.FromHexString(COLORS.BIOLUMINESCENT_GREEN);
    this.coreMat.emissiveColor = base.scale(
      (0.22 + pulse * 0.35) * revealBoost,
    );
    this.orbMat.emissiveColor = base.scale(
      (0.05 + pulse * 0.08) * (this.isAuraRevealed ? 2.2 : 1.0),
    );
    this.glowLight.intensity = this.presentationEnabled
      ? (0.25 + pulse * 0.35) * revealBoost * this.presentationScale : 0;
  }

  private clearNode(): void {
    this.cleared = true;
    this.state = "solved";
    this.coreMat.emissiveColor = Color3.FromHexString(COLORS.EMERALD);
    this.orbMat.emissiveColor = Color3.FromHexString(COLORS.EMERALD).scale(
      0.4,
    );
    this.glowLight.diffuse = Color3.FromHexString(COLORS.EMERALD);
    this.glowLight.intensity = this.presentationEnabled ? .1 * this.presentationScale : 0;
    gameEvents.emit(GameEvents.SUNDEW_NODE_CLEARED, {
      nodeKey: this.key,
      remaining: 0,
    });
  }

  override dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    this.glowLight.dispose();
    this.coreMat.dispose();
    super.dispose();
  }
}
